import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, ReferenceLine } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { useDestrierTheme, getCardSx } from '../ThemeContext';
import { useLang } from '../i18n';
import { useDataCache } from '../DataCacheContext';
import { API_BASE } from '../api';
import TimePeriodFilter from './TimePeriodFilter';

const NEMESIS_COLOR = '#E4446E';
const PREY_COLOR = '#00A5C4';

function WinRateBar({ wins, losses, draws, total, c, showCounts = false, height = 8 }) {
  const w = (wins / total) * 100;
  const l = (losses / total) * 100;
  const d = Math.max(0, 100 - w - l);
  // Only show a count inside a segment if that segment is wide enough
  // (≥ ~9% of the bar) — avoids cramped/overlapping labels for tiny slices.
  const MIN_PCT = 9;
  const labelSx = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontWeight: 800, fontSize: height >= 24 ? '1rem' : height >= 20 ? '0.95rem' : '0.75rem',
    height: '100%',
    overflow: 'hidden', whiteSpace: 'nowrap',
    letterSpacing: '0.02em',
  };
  return (
    <Box sx={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', height, width: '100%' }}>
      <Box sx={{ width: `${w}%`, backgroundColor: c.blue, ...labelSx }}>
        {showCounts && w >= MIN_PCT ? `${wins}W` : ''}
      </Box>
      <Box sx={{ width: `${d}%`, backgroundColor: `${c.textMuted}25`, ...labelSx, color: c.textDark }}>
        {showCounts && d >= MIN_PCT ? `${draws}D` : ''}
      </Box>
      <Box sx={{ width: `${l}%`, backgroundColor: '#E4446E', ...labelSx }}>
        {showCounts && l >= MIN_PCT ? `${losses}L` : ''}
      </Box>
    </Box>
  );
}

// Icons — pure SVG so they take the current theme color (no emoji tints).
function SkullIcon({ color, size = 26 }) {
  // Clean Lucide-style skull: cranium + jaw + large eye sockets + nose cavity + teeth.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a8 8 0 0 0-8 8c0 2.8 1.4 5.2 3.5 6.7V19a2 2 0 0 0 2 2h5a2 2 0 0 0 2-2v-2.3A8 8 0 0 0 12 2Z" fill={`${color}18`} />
      <circle cx="9" cy="11" r="2" fill={color} stroke="none" />
      <circle cx="15" cy="11" r="2" fill={color} stroke="none" />
      <path d="M12 14.5l-1 2h2l-1-2Z" fill={color} stroke="none" />
      <path d="M10 21v-2M12 21v-2M14 21v-2" />
    </svg>
  );
}

function TargetIcon({ color, size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" fill={`${color}15`} />
      <circle cx="12" cy="12" r="5.5" />
      <circle cx="12" cy="12" r="2" fill={color} stroke="none" />
      <path d="M12 1.5v3.5M12 19v3.5M1.5 12H5M19 12h3.5" />
    </svg>
  );
}

function RecentFormDots({ form, c }) {
  if (!form || !form.length) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      {form.map((r, i) => {
        const bg = r === 'W' ? c.blue : r === 'L' ? NEMESIS_COLOR : `${c.textMuted}60`;
        return (
          <Box key={i} title={r === 'W' ? 'Win' : r === 'L' ? 'Loss' : 'Draw'}
            sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 800, color: '#fff' }}>
            {r}
          </Box>
        );
      })}
    </Box>
  );
}

function RivalCard({ opp, type, c, cardSx, topStripColor, accentColor, rank = 1, total = 1 }) {
  const Icon = type === 'nemesis' ? SkullIcon : TargetIcon;
  const baseLabel = type === 'nemesis' ? 'Nemesis' : 'Favorite Prey';
  const label = total > 1 ? `#${rank} ${baseLabel}` : (type === 'nemesis' ? 'Your Nemesis' : baseLabel);
  const tagline = type === 'nemesis'
    ? `Study your games against ${opp.opponent} to find the pattern — especially in the opening.`
    : `You dominate ${opp.opponent}. Keep doing what works.`;
  return (
    <Box sx={{ ...cardSx, p: 3, border: `2px solid ${accentColor}25`, position: 'relative', overflow: 'hidden', width: '100%' }}>
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, backgroundColor: topStripColor, borderRadius: '20px 20px 0 0' }} />
      {/* Rank badge — top-right */}
      {total > 1 && (
        <Box sx={{
          position: 'absolute', top: 14, right: 14,
          px: 1.2, py: 0.4, borderRadius: '999px',
          backgroundColor: `${accentColor}15`, border: `1.5px solid ${accentColor}40`,
          fontSize: '0.78rem', fontWeight: 800, color: accentColor,
          letterSpacing: '0.06em',
        }}>
          #{rank} of {total}
        </Box>
      )}
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, pr: total > 1 ? 10 : 0 }}>
        <Box sx={{ width: 48, height: 48, borderRadius: '14px', bgcolor: `${accentColor}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon color={accentColor} size={26} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: accentColor, fontSize: '0.85rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{label}</Typography>
          <Typography sx={{ color: c.textDark, fontSize: '1.4rem', fontWeight: 900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opp.opponent}</Typography>
        </Box>

        {/* Total games played — absolute center so it stays dead-middle regardless of name length */}
        <Box sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          px: 1.4, py: 0.7,
          borderRadius: '12px',
          backgroundColor: `${accentColor}10`,
          border: `1px solid ${accentColor}25`,
          pointerEvents: 'none',
        }}>
          <Typography sx={{ color: accentColor, fontSize: '1.15rem', fontWeight: 900, lineHeight: 1 }}>{opp.games}</Typography>
          <Typography sx={{ color: c.textMuted, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mt: 0.3 }}>Games</Typography>
        </Box>

        {opp.recent_form && opp.recent_form.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
            <Typography sx={{ color: c.textMuted, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
              Last {opp.recent_form.length} Games
            </Typography>
            <RecentFormDots form={opp.recent_form} c={c} />
          </Box>
        )}
      </Box>

      <WinRateBar wins={opp.wins} losses={opp.losses} draws={opp.draws} total={opp.games} c={c} showCounts height={22} />

      {/* Current streak — how many same-outcome games in a row, from the most recent game */}
      {(() => {
        const form = opp.recent_form || [];
        let streakCount = 0;
        let streakChar = null;
        for (const r of form) {
          if (streakChar === null) {
            if (r === 'W' || r === 'L') { streakChar = r; streakCount = 1; }
            else break; // draw breaks the concept of a win/loss streak
          } else if (r === streakChar) {
            streakCount += 1;
          } else {
            break;
          }
        }
        const streakLabel = streakChar === 'W'
          ? `${streakCount}W streak`
          : streakChar === 'L'
          ? `${streakCount}L streak`
          : '—';
        const streakColor = streakChar === 'W'
          ? c.blue
          : streakChar === 'L'
          ? NEMESIS_COLOR
          : c.textMuted;
        return (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mt: 2 }}>
            <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: '12px', bgcolor: `${accentColor}08`, border: `1px solid ${accentColor}12` }}>
              <Typography sx={{ color: accentColor, fontSize: '1.3rem', fontWeight: 900 }}>{(opp.win_rate * 100).toFixed(0)}%</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: '0.88rem', fontWeight: 600 }}>Win Rate</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: '12px', bgcolor: `${accentColor}08`, border: `1px solid ${accentColor}12` }}>
              <Typography sx={{ color: accentColor, fontSize: '1.3rem', fontWeight: 900 }}>{type === 'nemesis' ? opp.losses : opp.wins}</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: '0.88rem', fontWeight: 600 }}>{type === 'nemesis' ? 'Losses' : 'Wins'}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: '12px', bgcolor: `${c.textMuted}06`, border: `1px solid ${c.textMuted}12` }}>
              <Typography sx={{ color: streakColor, fontSize: '1.3rem', fontWeight: 900 }}>{streakLabel}</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: '0.88rem', fontWeight: 600 }}>Current Streak</Typography>
            </Box>
          </Box>
        );
      })()}

      {/* Color split */}
      {opp.color_split && (opp.color_split.white.games > 0 || opp.color_split.black.games > 0) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1.5 }}>
          {['white', 'black'].map(col => {
            const s = opp.color_split[col];
            if (!s || !s.games) return <Box key={col} />;
            return (
              <Box key={col} sx={{ p: 1.2, borderRadius: '10px', bgcolor: `${c.textMuted}04`, border: `1px solid ${c.textMuted}10` }}>
                <Typography sx={{ color: c.textMuted, fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>As {col === 'white' ? 'White' : 'Black'}</Typography>
                <Typography sx={{ color: c.textDark, fontSize: '0.98rem', fontWeight: 700 }}>
                  <span style={{ color: c.blue }}>{s.wins}W</span>{' '}
                  <span style={{ color: c.textMuted }}>{s.draws}D</span>{' '}
                  <span style={{ color: NEMESIS_COLOR }}>{s.losses}L</span>
                  <span style={{ color: accentColor, fontWeight: 800, marginLeft: 6 }}>{Math.round((s.win_rate || 0) * 100)}%</span>
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Elo expectation: a plain-language take on expected vs actual win rate */}
      {opp.expected_score !== undefined && (
        <Box sx={{ mt: 1.5, p: 1.2, borderRadius: '10px', bgcolor: `${c.textMuted}04`, border: `1px solid ${c.textMuted}10` }}>
          <Typography sx={{ color: c.textMuted, fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Elo Expectation</Typography>
          <Typography sx={{ color: c.textDark, fontSize: '0.98rem', fontWeight: 600, lineHeight: 1.5 }}>
            {(() => {
              const expPct = Math.round((opp.expected_score || 0) * 100);
              const actPct = Math.round((opp.actual_score || 0) * 100);
              const diff = actPct - expPct;
              const dir = diff >= 0 ? 'above' : 'below';
              const diffCol = diff >= 0 ? c.blue : NEMESIS_COLOR;
              return (
                <>Based on their rating, you'd be expected to win around <strong style={{ color: c.textDark }}>{expPct}%</strong> of the games against <strong style={{ color: c.textDark }}>{opp.opponent}</strong>. You're actually winning <strong style={{ color: accentColor }}>{actPct}%</strong>{' '}
                <span style={{ color: diffCol, fontWeight: 700 }}>({Math.abs(diff)}% {dir} expectation)</span>.</>
              );
            })()}
          </Typography>
        </Box>
      )}

      {/* Head-to-head top opening */}
      {opp.top_opening && opp.top_opening.games >= 2 && (
        <Box sx={{ mt: 1.5, p: 1.2, borderRadius: '10px', bgcolor: `${c.textMuted}04`, border: `1px solid ${c.textMuted}10` }}>
          <Typography sx={{ color: c.textMuted, fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Most-Played Opening</Typography>
          <Typography sx={{ color: c.textDark, fontSize: '0.98rem', fontWeight: 700 }}>
            {opp.top_opening.name} <span style={{ color: c.textMuted, fontWeight: 500 }}>({opp.top_opening.games} games:</span>{' '}
            <span style={{ color: c.blue }}>{opp.top_opening.wins}W</span>{' '}
            <span style={{ color: c.textMuted }}>{opp.top_opening.draws}D</span>{' '}
            <span style={{ color: NEMESIS_COLOR }}>{opp.top_opening.losses}L</span><span style={{ color: c.textMuted, fontWeight: 500 }}>)</span>
          </Typography>
        </Box>
      )}

      <Typography sx={{ color: c.textMuted, fontSize: '1rem', lineHeight: 1.55, mt: 2, fontStyle: 'italic' }}>
        {tagline}
      </Typography>

      {opp.last_game_url && (
        <Box sx={{ mt: 1.5 }}>
          <Typography
            component="a"
            href={opp.last_game_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: accentColor, fontSize: '0.95rem', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            View last game →
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// Shows one active rival card at a time, with a clear rank tab selector and
// prev/next arrows so the user always knows there are other rivals to cycle
// through. Swaps cards with a slide-fade animation.
function RivalStack({ opponents, type, c, cardSx, topStripColor, accentColor }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const cards = opponents.slice(0, 3);
  const N = cards.length;
  if (N === 0) return null;
  const goPrev = () => setActiveIdx((activeIdx - 1 + N) % N);
  const goNext = () => setActiveIdx((activeIdx + 1) % N);
  const ArrowBtn = ({ disabled, onClick, children }) => (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        width: 34, height: 34, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: `1.5px solid ${disabled ? `${c.textMuted}25` : `${accentColor}40`}`,
        color: disabled ? `${c.textMuted}80` : accentColor,
        backgroundColor: disabled ? 'transparent' : `${accentColor}08`,
        cursor: disabled ? 'default' : 'pointer',
        transition: 'all 0.15s',
        fontSize: '1.25rem', fontWeight: 800, lineHeight: 1,
        userSelect: 'none',
        '&:hover': disabled ? {} : { backgroundColor: `${accentColor}18` },
      }}
    >
      {children}
    </Box>
  );
  return (
    <Box sx={{ width: '100%' }}>
      {/* Active card — slides when switching */}
      <Box sx={{ position: 'relative', width: '100%', minHeight: 520 }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={cards[activeIdx].opponent}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.2 }}
            style={{ width: '100%' }}
          >
            <RivalCard
              opp={cards[activeIdx]}
              type={type}
              c={c}
              cardSx={cardSx}
              topStripColor={topStripColor}
              accentColor={accentColor}
              rank={activeIdx + 1}
              total={N}
            />
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Rank selector: prev / #1 #2 #3 / next */}
      {N > 1 && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.8 }}>
          <Typography sx={{ color: c.textMuted, fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Switch between your top {N} {type === 'nemesis' ? 'nemesis' : 'prey'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <ArrowBtn disabled={activeIdx === 0} onClick={goPrev}>‹</ArrowBtn>
          <Box sx={{ display: 'flex', gap: 0.8 }}>
            {cards.map((opp, i) => {
              const active = i === activeIdx;
              return (
                <Box
                  key={opp.opponent}
                  onClick={() => setActiveIdx(i)}
                  sx={{
                    px: 2, py: 0.8, borderRadius: '999px',
                    fontSize: '0.9rem', fontWeight: 700,
                    backgroundColor: active ? accentColor : 'transparent',
                    color: active ? '#fff' : c.textMuted,
                    border: `1.5px solid ${active ? accentColor : `${c.textMuted}30`}`,
                    cursor: 'pointer',
                    transition: 'all 0.18s',
                    whiteSpace: 'nowrap',
                    '&:hover': active ? {} : { borderColor: accentColor, color: accentColor },
                  }}
                >
                  #{i + 1}
                </Box>
              );
            })}
          </Box>
            <ArrowBtn disabled={activeIdx === N - 1} onClick={goNext}>›</ArrowBtn>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default function OpponentsPage() {
  const { c } = useDestrierTheme();
  const { t } = useLang();
  const cardSx = getCardSx(c);
  const cache = useDataCache();
  const [searchParams] = useSearchParams();
  const username = searchParams.get('username');
  const [period, setPeriod] = useState('all');
  const [data, setData] = useState(null);
  const [dataAllTime, setDataAllTime] = useState(null);
  const [phase, setPhase] = useState('fetching');
  const [eloRangeGameType, setEloRangeGameType] = useState('all');
  const [, setError] = useState(null);

  useEffect(() => {
    if (!username) return;
    const ck = `opponents:${username}:${period}`;
    const hit = cache.get(ck);
    if (hit) { setData(hit); setPhase('dashboard'); return; }
    setError(null);
    setPhase('fetching');
    let cancelled = false;
    fetch(`${API_BASE}/opponents?username=${username}&period=${period}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(d => { if (!cancelled) { cache.set(ck, d); setData(d); setPhase('dashboard'); } })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [username, period]); // eslint-disable-line react-hooks/exhaustive-deps

  // All-time data fetch: used exclusively for the Performance by Rating Range
  // section so rating-based stats stay stable when the user flips the top
  // period filter. Skip the extra fetch when the user is already on 'all' —
  // the primary fetch already has the all-time data.
  useEffect(() => {
    if (!username) return;
    if (period === 'all') return; // reuse `data` below instead of double-fetching
    const ck = `opponents:${username}:all`;
    const hit = cache.get(ck);
    if (hit) { setDataAllTime(hit); return; }
    let cancelled = false;
    fetch(`${API_BASE}/opponents?username=${username}&period=all`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(d => { if (!cancelled) { cache.set(ck, d); setDataAllTime(d); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [username, period]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!username) {
    return <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Typography sx={{ color: c.textMuted }}>{t.pleaseProvideUsername}</Typography></Box>;
  }

  const d = data;
  // When the user is on 'all', `data` IS the all-time data — no need for a second fetch.
  // Otherwise, fall back to `data` until the all-time fetch resolves so nothing renders blank.
  const dAll = period === 'all' ? data : (dataAllTime || data);

  return (
    <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg }}>
      {phase === 'fetching' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid ${c.accent}20`, borderTopColor: c.accent, animation: 'spin 0.8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
        </Box>
      )}

      {phase === 'dashboard' && d && (
        <Box sx={{ px: { xs: 2, md: 3, lg: 4 }, pb: 6, pt: { xs: 9, md: 10 } }}>
          <Box sx={{ position: 'fixed', top: { xs: 56, md: 72 }, left: { xs: 0, md: 0 }, right: { xs: 0, md: '35px' }, display: 'flex', justifyContent: 'center', zIndex: 40, py: 1, pointerEvents: 'none' }}>
            <Box sx={{ pointerEvents: 'auto' }}>
              <TimePeriodFilter value={period} onChange={setPeriod} />
            </Box>
          </Box>

          {/* Page Header */}
          <Box sx={{ mb: 5 }}>
            <Typography sx={{ color: c.textDark, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.6rem' }, fontWeight: 900, letterSpacing: '-0.03em', mb: 1 }}>
              Rivals
            </Typography>
            <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7 }}>
              Chess is a two-player game — your opponents matter as much as your skills.
              This page shows how you perform against different rating ranges, who your nemesis is,
              and whether you're a giant killer or someone who crumbles under pressure.
              Use these insights to prepare differently for higher vs lower-rated opponents.
            </Typography>
          </Box>

          {/* ── Performance by Rating Range (unified: 5 stat tiles + chart, shared filter).
              Always reads all-time data (dAll) so the rating stats don't jump around
              when the user changes the top period filter. ── */}
          {(() => {
            const byType = dAll.by_elo_range_by_type || {};
            const strengthByType = dAll.strength_by_type || {};
            // Union of available game types across both shapes
            const typeSet = new Set([...Object.keys(byType), ...Object.keys(strengthByType)]);
            const availableTypes = Array.from(typeSet).filter(t => t !== 'daily' && t !== 'unknown');
            const activeRange = eloRangeGameType === 'all'
              ? dAll.by_elo_range
              : (byType[eloRangeGameType] || []);
            const activeBundle = eloRangeGameType === 'all'
              ? { by_strength: dAll.by_strength, giant_killer: dAll.giant_killer, choking: dAll.choking }
              : (strengthByType[eloRangeGameType] || { by_strength: {}, giant_killer: {}, choking: {} });
            const chartData = (activeRange || []).filter(r => r.games >= 3);
            return (
          <Box sx={{ mb: 5 }}>
            <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 800, mb: 0.5 }}>Performance by Rating Range</Typography>
            <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 2 }}>
              Your all-time win rate against stronger, equal, and weaker opponents — plus your most impressive upset and the most painful surprise loss. Use the time controls below to filter by game type; the top period filter doesn't affect this section.
            </Typography>
            {/* Shared game type filter: drives both the stat tiles and the chart */}
            {availableTypes.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {['all', ...availableTypes].map(gt => {
                  const active = eloRangeGameType === gt;
                  const label = gt === 'all' ? 'All' : gt.charAt(0).toUpperCase() + gt.slice(1);
                  return (
                    <Box
                      key={gt}
                      onClick={() => setEloRangeGameType(gt)}
                      sx={{
                        px: 2, py: 0.7, borderRadius: '999px', cursor: 'pointer',
                        fontSize: '0.92rem', fontWeight: 700,
                        backgroundColor: active ? c.accent : 'transparent',
                        color: active ? '#fff' : c.textMuted,
                        border: `1.5px solid ${active ? c.accent : `${c.textMuted}30`}`,
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: c.accent, color: active ? '#fff' : c.accent },
                      }}
                    >
                      {label}
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* 5 stat tiles — filtered by the same game type as the chart */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
              {[
                { label: 'vs Higher Rated', value: `${(activeBundle.by_strength?.higher?.win_rate * 100 || 0).toFixed(0)}%`, sub: `${activeBundle.by_strength?.higher?.games || 0} games`, color: c.blue },
                { label: 'vs Similar', value: `${(activeBundle.by_strength?.similar?.win_rate * 100 || 0).toFixed(0)}%`, sub: `${activeBundle.by_strength?.similar?.games || 0} games`, color: c.textDark },
                { label: 'vs Lower Rated', value: `${(activeBundle.by_strength?.lower?.win_rate * 100 || 0).toFixed(0)}%`, sub: `${activeBundle.by_strength?.lower?.games || 0} games`, color: c.accent },
                {
                  label: 'Biggest Upset',
                  value: activeBundle.giant_killer?.biggest_upset_opp ? `+${activeBundle.giant_killer.biggest_upset} Elo` : '—',
                  sub: activeBundle.giant_killer?.biggest_upset_opp ? `vs ${activeBundle.giant_killer.biggest_upset_opp.opponent} (~${activeBundle.giant_killer.biggest_upset_opp.opp_elo})` : 'No upsets yet',
                  color: c.blue,
                },
                {
                  label: 'Biggest Loss',
                  value: activeBundle.choking?.biggest_loss_opp ? `${activeBundle.choking.biggest_loss} Elo` : '—',
                  sub: activeBundle.choking?.biggest_loss_opp ? `vs ${activeBundle.choking.biggest_loss_opp.opponent} (~${activeBundle.choking.biggest_loss_opp.opp_elo})` : 'No surprise losses',
                  color: '#E4446E',
                },
              ].map((s) => (
                <Box key={s.label} sx={{ ...cardSx, p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                  <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 500, mb: 0.5 }}>{s.label}</Typography>
                  <Typography sx={{ color: s.color, fontSize: { xs: '1.3rem', sm: '1.6rem' }, fontWeight: 800, lineHeight: 1.2 }}>{s.value}</Typography>
                  {s.sub && <Typography sx={{ color: c.textMuted, fontSize: '0.98rem', mt: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.sub}</Typography>}
                </Box>
              ))}
            </Box>

            <Box sx={{ ...cardSx, p: { xs: 1.5, sm: 3 }, height: { xs: 320, sm: 380, md: 440 }, display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ fontSize: '1.05rem', color: c.textMuted, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1 }}>
                Win Rate by Elo Range {eloRangeGameType !== 'all' && `— ${eloRangeGameType.charAt(0).toUpperCase() + eloRangeGameType.slice(1)}`}
              </Typography>
              {chartData.length === 0 ? (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>Not enough games in this time control.</Typography>
                </Box>
              ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 25, right: 10, bottom: 5, left: 10 }}>
                  <CartesianGrid stroke={`${c.textMuted}12`} strokeDasharray="3 3" />
                  <XAxis dataKey="elo_range" tick={{ fill: c.textMuted, fontSize: 14, fontWeight: 500 }} tickLine={false} axisLine={{ stroke: `${c.textMuted}20` }} interval={0} />
                  <YAxis tick={{ fill: c.textMuted, fontSize: 14, fontWeight: 500 }} tickLine={false} axisLine={{ stroke: `${c.textMuted}20` }} domain={[0, 1]} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} width={50} />
                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const r = payload[0].payload;
                    return (
                      <Box sx={{ background: c.bg, border: `1px solid ${c.textMuted}25`, borderRadius: '12px', p: 2, boxShadow: `0 4px 16px ${c.textMuted}15` }}>
                        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mb: 0.5 }}>{r.elo_range}</Typography>
                        <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 700 }}>{(r.win_rate * 100).toFixed(0)}% win rate</Typography>
                        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>{r.wins}W / {r.draws}D / {r.losses}L ({r.games} games)</Typography>
                      </Box>
                    );
                  }} />
                  <ReferenceLine y={0.5} stroke={c.textMuted} strokeDasharray="6 4" strokeOpacity={0.5} label={{ value: '50%', position: 'right', fill: c.textMuted, fontSize: 13, fontWeight: 600 }} />
                  <Bar dataKey="win_rate" radius={[6, 6, 0, 0]} animationDuration={800} label={({ x, y, width, value, index }) => {
                    const r = chartData[index];
                    const color = r?.win_rate >= 0.55 ? c.blue : r?.win_rate < 0.4 ? '#E4446E' : c.textMuted;
                    return <text x={x + width / 2} y={y - 8} fill={color} textAnchor="middle" fontSize={15} fontWeight={800}>{`${(value * 100).toFixed(0)}%`}</text>;
                  }}>
                    {chartData.map((r, i) => (
                      <Cell key={i} fill={r.win_rate >= 0.55 ? c.blue : r.win_rate < 0.4 ? '#E4446E' : c.textMuted} fillOpacity={0.7} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              )}
            </Box>
          </Box>
            );
          })()}

          {/* ── Nemesis & Favorite Prey ──
              Uses the top period filter's data. Shows an empty-state message
              (instead of silently disappearing) when no rivals hit the
              threshold in the selected window — common for short periods. */}
          {(() => {
            const opps = d.recurring_opponents || [];
            const withGames = opps.filter(o => o.games >= 3);
            const nemesisList = [...withGames].filter(o => o.win_rate < 0.5).sort((a, b) => a.win_rate - b.win_rate).slice(0, 3);
            const preyList = [...withGames].filter(o => o.win_rate > 0.5).sort((a, b) => b.win_rate - a.win_rate).slice(0, 3);
            return (
              <Box sx={{ mb: 5 }}>
                <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 800, mb: 0.5 }}>Nemesis & Prey</Typography>
                <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 3 }}>
                  Your top 3 nemesis and prey — click the peeking cards to cycle through them.
                  If you keep running into a nemesis, study their style; there's a pattern you're not seeing.
                </Typography>
                {nemesisList.length === 0 && preyList.length === 0 ? (
                  <Box sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                    <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.6 }}>
                      No recurring rivals in this time window yet — you need at least 3 games against the same player.
                      {period !== 'all' && ' Try switching the top filter to a longer period (or "All time") to see your rivals.'}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                    {nemesisList.length > 0 && (
                      <RivalStack
                        opponents={nemesisList}
                        type="nemesis"
                        c={c}
                        cardSx={cardSx}
                        topStripColor={NEMESIS_COLOR}
                        accentColor={NEMESIS_COLOR}
                      />
                    )}
                    {preyList.length > 0 && (
                      <RivalStack
                        opponents={preyList}
                        type="prey"
                        c={c}
                        cardSx={cardSx}
                        topStripColor={PREY_COLOR}
                        accentColor={PREY_COLOR}
                      />
                    )}
                  </Box>
                )}
              </Box>
            );
          })()}

          {/* ── Sweet Spot Insight ──
              Also uses all-time data so it stays consistent with the section above. */}
          {(() => {
            const eligible = (dAll.by_elo_range || []).filter(r => r.games >= 5);
            if (eligible.length === 0) return null;
            const best = eligible.reduce((a, b) => a.win_rate > b.win_rate ? a : b);
            return (
              <Box sx={{ ...cardSx, p: 3, mb: 5, border: `2px solid ${c.accent}25` }}>
                <Typography sx={{ color: c.textDark, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7 }}>
                  Your sweet spot is the <strong style={{ color: c.accent }}>{best.elo_range}</strong> bracket where you win <strong style={{ color: c.accent }}>{(best.win_rate * 100).toFixed(0)}%</strong> of games. You perform best when facing opponents in this range.
                </Typography>
              </Box>
            );
          })()}

          {/* ── Recurring Opponents ── */}
          <Box>
            <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 800, mb: 0.5 }}>Recurring Rivals</Typography>
            <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 3 }}>
              Opponents you've faced multiple times. These are the players who know your style — and you know theirs.
              Study your record against each one and look for patterns in the games you lose.
            </Typography>
            {(!d.recurring_opponents || d.recurring_opponents.length === 0) ? (
              <Box sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', lineHeight: 1.6 }}>
                  No recurring rivals in this time window yet — you need at least 3 games against the same player.
                  {period !== 'all' && ' Try switching the top filter to a longer period (or "All time") to see your rivals.'}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {d.recurring_opponents.map((opp) => {
                  const wrColor = opp.win_rate >= 0.55 ? c.blue : opp.win_rate < 0.4 ? '#E4446E' : c.textMuted;
                  const ws = opp.color_split?.white;
                  const bs = opp.color_split?.black;
                  return (
                  <Box key={opp.opponent} sx={{ ...cardSx, p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                          <Typography sx={{ color: c.textDark, fontSize: '1.1rem', fontWeight: 700 }}>{opp.opponent}</Typography>
                          {opp.recent_form && opp.recent_form.length > 0 && (
                            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ color: c.textMuted, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                Last {opp.recent_form.length}
                              </Typography>
                              <RecentFormDots form={opp.recent_form} c={c} />
                            </Box>
                          )}
                        </Box>
                        <WinRateBar wins={opp.wins} losses={opp.losses} draws={opp.draws} total={opp.games} c={c} showCounts height={20} />
                      </Box>
                      <Box sx={{ textAlign: 'center', flexShrink: 0, minWidth: 68 }}>
                        <Typography sx={{ color: wrColor, fontSize: '1.3rem', fontWeight: 800 }}>
                          {(opp.win_rate * 100).toFixed(0)}%
                        </Typography>
                        <Typography sx={{ color: c.textMuted, fontSize: '0.92rem' }}>{opp.games} games</Typography>
                      </Box>
                    </Box>

                    {/* Extra details: color split, most-played opening, view-last-game */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 1.2, mt: 1.5 }}>
                      {(ws?.games > 0 || bs?.games > 0) && (
                        <Box sx={{ p: 1.2, borderRadius: '10px', bgcolor: `${c.textMuted}04`, border: `1px solid ${c.textMuted}10` }}>
                          <Typography sx={{ color: c.textMuted, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.3 }}>By Color</Typography>
                          <Typography sx={{ color: c.textDark, fontSize: '0.93rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                            {ws?.games > 0 && (
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', bgcolor: '#fff', border: `1.5px solid ${c.textMuted}50` }} />
                                <span style={{ color: c.blue }}>{ws.wins}W</span> <span style={{ color: '#E4446E' }}>{ws.losses}L</span> <span style={{ color: c.textMuted }}>({ws.games})</span>
                              </Box>
                            )}
                            {ws?.games > 0 && bs?.games > 0 && <span style={{ color: c.textMuted }}>·</span>}
                            {bs?.games > 0 && (
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', bgcolor: '#1a1a1a', border: `1.5px solid ${c.textMuted}50` }} />
                                <span style={{ color: c.blue }}>{bs.wins}W</span> <span style={{ color: '#E4446E' }}>{bs.losses}L</span> <span style={{ color: c.textMuted }}>({bs.games})</span>
                              </Box>
                            )}
                          </Typography>
                        </Box>
                      )}
                      {opp.top_opening && opp.top_opening.games >= 2 && (
                        <Box sx={{ p: 1.2, borderRadius: '10px', bgcolor: `${c.textMuted}04`, border: `1px solid ${c.textMuted}10` }}>
                          <Typography sx={{ color: c.textMuted, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.3 }}>Most Played</Typography>
                          <Typography sx={{ color: c.textDark, fontSize: '0.93rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {opp.top_opening.name} <span style={{ color: c.textMuted, fontWeight: 500 }}>(</span>
                            <span style={{ color: c.blue }}>{opp.top_opening.wins}W</span>{' '}
                            <span style={{ color: '#E4446E' }}>{opp.top_opening.losses}L</span><span style={{ color: c.textMuted, fontWeight: 500 }}>)</span>
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ p: 1.2, borderRadius: '10px', bgcolor: `${c.textMuted}04`, border: `1px solid ${c.textMuted}10`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Box>
                          <Typography sx={{ color: c.textMuted, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.3 }}>
                            Elo Expectation
                          </Typography>
                          <Typography sx={{ color: c.textDark, fontSize: '0.93rem', fontWeight: 600, lineHeight: 1.45 }}>
                            {(() => {
                              const expPct = Math.round((opp.expected_score || 0) * 100);
                              const actPct = Math.round((opp.actual_score || 0) * 100);
                              const diff = actPct - expPct;
                              const dir = diff >= 0 ? 'above' : 'below';
                              const diffCol = diff >= 0 ? c.blue : '#E4446E';
                              return (
                                <>Based on their rating, you'd be expected to win around <strong style={{ color: c.textDark }}>{expPct}%</strong> of the games. You're actually winning <strong style={{ color: wrColor }}>{actPct}%</strong>
                                  {' '}<span style={{ color: diffCol, fontWeight: 700 }}>({Math.abs(diff)}% {dir} expectation)</span>.</>
                              );
                            })()}
                          </Typography>
                        </Box>
                        {opp.last_game_url && (
                          <Typography component="a" href={opp.last_game_url} target="_blank" rel="noopener noreferrer"
                            sx={{ color: wrColor, fontSize: '0.88rem', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}>
                            Last game →
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
